<!--
  Applications.svelte — Admin list page for managing registered applications.

  Lists all applications in a table with search/filter. Edit navigates to
  the dedicated ApplicationEdit detail page. Create uses a small modal
  then redirects to the detail page for further configuration.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Badge,
		Button,
		Input,
		Spinner,
		Modal,
		Label
	} from 'flowbite-svelte';
	import PageHeader from '../components/PageHeader.svelte';
	import AbstractDropdown from '../components/AbstractDropdown.svelte';
	import { ConfirmButton, Panel, RoutedButton, SortableTableHeadCell } from '../components';
	import { listApplications, createApplication, deleteApplication } from '../AppNetwork';
	import { toastSuccess, toastError } from '../toast';
	import { push } from '../abstract-spa-router';
	import type { Application, SortDirection } from '../AppTypes';
	import site from '../site';
	import { t, t2 } from '../i18n';

	/** All applications loaded from the server. */
	let applications: Application[] = $state([]);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error banner text. */
	let error = $state('');
	/** Search/filter text for the table. */
	let search = $state('');
	/** Status filter: '' = all, 'active', 'inactive'. */
	let filterStatus = $state('');

	// ── Sorting ───────────────────────────────────────────────────

	/** Active server-side sort key (`''` = server default, name ascending). */
	let sortBy = $state('');
	/** Active server-side sort direction. */
	let sortDir: SortDirection = $state('asc');

	/** Switch the sort column/direction and refetch from the server. */
	function handleSort(key: string, dir: SortDirection): void {
		sortBy = key;
		sortDir = dir;
		loadApplications();
	}

	/** Status filter dropdown items. */
	const statusFilterOptions = [
		{ value: '', name: t2('All statuses'), icon: 'fa-solid fa-list' },
		{ value: 'active', name: t2('Active'), icon: 'fa-solid fa-circle-check' },
		{ value: 'inactive', name: t2('Inactive'), icon: 'fa-solid fa-circle-xmark' }
	];

	// ── Create modal state ────────────────────────────────────────

	/** Whether the create modal is open. */
	let showCreateModal = $state(false);
	/** Whether a create is in progress. */
	let creating = $state(false);
	/** Name for the new application. */
	let createName = $state('');
	/** Description for the new application. */
	let createDescription = $state('');

	/** API base URL for constructing logo preview URLs. */
	const apiBase = site.backend.baseURL;

	// ── Data loading ──────────────────────────────────────────────

	/** Fetch all applications from the server. */
	async function loadApplications(): Promise<void> {
		loading = true;
		error = '';
		/* Fetch the full list of applications from the API. */
		try {
			applications = await listApplications({
				sortBy: sortBy || undefined,
				sortDir: sortBy ? sortDir : undefined
			});
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load applications');
		} finally {
			loading = false;
		}
	}

	// ── Filtered list ─────────────────────────────────────────────

	/** Whether any non-default filter is active. */
	let hasFilters = $derived(!!search || !!filterStatus);

	let filtered = $derived(applications.filter((a) => {
		/* Match application name or description against search text. */
		if (search) {
			const q = search.toLowerCase();
			if (!a.applicationName.toLowerCase().includes(q) && !(a.description || '').toLowerCase().includes(q))
				return false;
		}
		/* Exclude apps that don't match the active filter. */
		if (filterStatus === 'active' && !a.isActive) return false;
		/* Exclude apps that don't match the inactive filter. */
		if (filterStatus === 'inactive' && a.isActive) return false;
		return true;
	}));

	/** Clear all filters to defaults. */
	function clearFilters(): void {
		search = '';
		filterStatus = '';
	}

	// ── Handlers ──────────────────────────────────────────────────

	/** Open the create modal with defaults. */
	function openCreate(): void {
		createName = '';
		createDescription = '';
		showCreateModal = true;
	}

	/** Create a new application and redirect to its detail page. */
	async function handleCreate(): Promise<void> {
		if (!createName.trim()) return;

		creating = true;
		/* Create the application and redirect to its detail page. */
		try {
			const app = await createApplication({
				applicationName: createName.trim(),
				description: createDescription.trim() || null
			});
			showCreateModal = false;
			push(`/Applications/${app.id}`);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to create application'));
		} finally {
			creating = false;
		}
	}

	/** Execute the deletion of an application. */
	async function handleDelete(app: Application): Promise<void> {
		/* Delete the application and refresh the list. */
		try {
			await deleteApplication(app.id);
			toastSuccess(t2('Deleted {{name}}', { name: app.applicationName }));
			await loadApplications();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete application'));
		}
	}

	/** Format a date string for display. */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}

	onMount(loadApplications);
</script>

<PageHeader title={$t("Applications")} description={$t("Register and manage applications that use this identity provider.")} />

<!-- Filters -->
<Panel title={$t("Filters")} faIcon="filter" class="mb-6">
	<div class="flex flex-wrap items-center gap-3">
		<div class="max-w-xs flex-1">
			<Input bind:value={search} placeholder={$t("Search applications...")}>
				{#snippet left()}
					<i class="fa-solid fa-search text-gray-400"></i>
				{/snippet}
			</Input>
		</div>
		<AbstractDropdown items={statusFilterOptions} bind:value={filterStatus} class="w-44" />
		{#if hasFilters}
			<Button size="sm" color="alternative" onclick={clearFilters}>
				<i class="fa-solid fa-xmark me-1"></i>{$t("Clear")}
			</Button>
		{/if}
		<Button onclick={openCreate}>
			<i class="fa-solid fa-plus me-2"></i>{$t("New Application")}
		</Button>
	</div>
</Panel>

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="8" />
	</div>
{:else if error && applications.length === 0}
	<div class="py-12 text-center">
		<i class="fa-solid fa-exclamation-triangle mb-4 text-4xl text-red-500"></i>
		<p class="text-gray-500 dark:text-gray-400">{error}</p>
		<Button class="mt-4" onclick={loadApplications}>{$t("Retry")}</Button>
	</div>
{:else if filtered.length === 0}
	<div class="py-12 text-center">
		<i class="fa-solid fa-cubes mb-4 text-4xl text-gray-300 dark:text-gray-600"></i>
		<p class="text-gray-500 dark:text-gray-400">
			{hasFilters ? $t('No applications match your filters') : $t('No applications registered yet')}
		</p>
	</div>
{:else}
	{#if error}
		<div class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
		</div>
	{/if}

	<Table divClass="widget !p-1 relative" class="dataTable">
		<TableHead class="bg-gray-50 dark:bg-gray-700">
			<SortableTableHeadCell key="name" {sortBy} {sortDir} onsort={handleSort}>
				{$t("Application")}
			</SortableTableHeadCell>
			<TableHeadCell>{$t("UUID")}</TableHeadCell>
			<SortableTableHeadCell key="users" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Users")}
			</SortableTableHeadCell>
			<SortableTableHeadCell key="status" {sortBy} {sortDir} onsort={handleSort}>{$t("Status")}</SortableTableHeadCell>
			<SortableTableHeadCell key="created" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Created")}
			</SortableTableHeadCell>
			<TableHeadCell>{$t("Actions")}</TableHeadCell>
		</TableHead>
		<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
			{#each filtered as app (app.id)}
				<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
					<TableBodyCell class="font-medium text-gray-900 dark:text-white">
						<div class="flex items-center gap-3">
							{#if app.logoImageName}
								<img
									src="{apiBase}/uploads/applications/{app.logoImageName}"
									alt="{app.applicationName} logo"
									class="h-8 w-8 rounded object-contain dark:invert"
								/>
							{:else if app.iconImageName}
								<img
									src="{apiBase}/uploads/applications/{app.iconImageName}"
									alt="{app.applicationName} icon"
									class="h-8 w-8 rounded object-contain dark:invert"
								/>
							{:else if app.icon}
								<i class="fa-solid {app.icon} text-lg text-primary-500"></i>
							{:else}
								<i class="fa-solid fa-cube text-lg text-gray-400"></i>
							{/if}
							<div>
								<div>{app.applicationName}</div>
								{#if app.description}
									<div class="max-w-[200px] truncate text-xs text-gray-500 dark:text-gray-400">
										{app.description}
									</div>
								{/if}
							</div>
						</div>
					</TableBodyCell>
					<TableBodyCell>
						<code class="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
							>{app.applicationUUID.substring(0, 8)}…</code
						>
					</TableBodyCell>
					<TableBodyCell>
						<span class="font-medium text-gray-700 dark:text-gray-300">{app.userCount ?? 0}</span>
					</TableBodyCell>
					<TableBodyCell>
						<div class="flex flex-wrap gap-1">
							{#if app.isActive}
								<Badge color="green">{$t("Active")}</Badge>
							{:else}
								<Badge color="red">{$t("Inactive")}</Badge>
							{/if}
							{#if app.isAlwaysVisible}
								<Badge color="blue">{$t("Visible")}</Badge>
							{/if}
							{#if app.isAutomaticApprovalEnabled}
								<Badge color="purple">{$t("Auto-approve")}</Badge>
							{/if}
						</div>
					</TableBodyCell>
					<TableBodyCell class="text-gray-500 dark:text-gray-400">
						{formatDate(app.created)}
					</TableBodyCell>
					<TableBodyCell>
						<div class="flex gap-1">
							<RoutedButton size="xs" color="alternative" href="/Applications/{app.id}">
								<i class="fa-solid fa-pen-to-square mr-1 h-3 w-3"></i>{$t("Edit")}
							</RoutedButton>
							<ConfirmButton
								size="xs"
								color="red"
								outline
								action={$t("delete this application")}
								onconfirm={() => handleDelete(app)}
							>
								<i class="fa-solid fa-trash text-xs"></i>
							</ConfirmButton>
						</div>
					</TableBodyCell>
				</TableBodyRow>
			{/each}
		</TableBody>
	</Table>
{/if}

<!-- Create Application Modal -->
<Modal bind:open={showCreateModal} title={$t("New Application")} size="sm" autoclose={false}>
	<div class="space-y-4">
		<div>
			<Label class="mb-1">{$t("Application Name")} *</Label>
			<Input bind:value={createName} placeholder={$t("My Application")} />
		</div>
		<div>
			<Label class="mb-1">{$t("Description")}</Label>
			<Input bind:value={createDescription} placeholder={$t("Brief description (optional)")} />
		</div>
	</div>

	{#snippet footer()}
		<div class="flex gap-2">
			<Button color="alternative" onclick={() => (showCreateModal = false)}>{$t("Cancel")}</Button>
			<Button
				color="primary"
				disabled={creating || !createName.trim()}
				onclick={handleCreate}
			>
				{#if creating}<Spinner size="4" class="mr-2" />{/if}
				{$t("Create Application")}
			</Button>
		</div>
	{/snippet}
</Modal>
